import React, { useContext, useEffect, useState } from "react";
import { Card, Col, Row, Statistic, Tabs, Typography, message, theme } from "antd";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { hasPermission } from "../../../../commons/Permissions/PermissionsHook";
import { UserContext } from "../../../../context/UserContext";
import { errorText } from "../api";
import PageHeader from "../shared/PageHeader";
import { formatCents } from "../shared/money";
import { listAccounts, reconciliationSummary } from "./api";
import AccountsTab from "./AccountsTab";
import TransactionsTab from "./TransactionsTab";
import PaymentsTab from "./PaymentsTab";

const MODULE = "Accounting";
const TAB_KEYS = ["accounts", "transactions", "payments"];

/** Reconciliation card: bank balance vs ledger, open lines. */
const ReconciliationCard = ({ summary }) => {
  const { t } = useTranslation("accounting");
  const { token } = theme.useToken();
  if (!summary?.totals) return null;
  const { totals } = summary;
  const open = (totals.counts?.unmatched || 0) + (totals.counts?.suggested || 0);
  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      <Col xs={12} md={6}>
        <Card size="small" className="acct-kpi">
          <Statistic title={t("Bank balance")} value={formatCents(totals.bankBalance)} />
        </Card>
      </Col>
      <Col xs={12} md={6}>
        <Card size="small" className="acct-kpi">
          <Statistic title={t("Ledger balance")} value={formatCents(totals.ledgerBalance)} />
        </Card>
      </Col>
      <Col xs={12} md={6}>
        <Card size="small" className="acct-kpi">
          <Statistic
            title={t("Difference")}
            value={formatCents(totals.difference)}
            valueStyle={{
              color: totals.difference === 0 ? token.colorSuccess : token.colorError
            }}
          />
        </Card>
      </Col>
      <Col xs={12} md={6}>
        <Card size="small" className="acct-kpi">
          <Statistic
            title={t("Open transactions")}
            value={open}
            valueStyle={{ color: open ? token.colorWarning : token.colorSuccess }}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {totals.counts?.suggested || 0} {t("with suggestion")} ·{" "}
            {(totals.counts?.matched || 0) + (totals.counts?.split || 0)} {t("matched")}
          </Typography.Text>
        </Card>
      </Col>
    </Row>
  );
};

/** Bank & payments: accounts, transactions (Zahlungsabgleich), payment runs. */
const Bank = () => {
  const { t } = useTranslation("accounting");
  const user = useContext(UserContext);
  const history = useHistory();
  const location = useLocation();
  const canWrite =
    hasPermission(user?.permissions, MODULE, "Update") ||
    hasPermission(user?.permissions, MODULE, "Create");
  const [accounts, setAccounts] = useState([]);
  const [presets, setPresets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const segment = location.pathname.split("/user/accounting/bank/")[1]?.split("/")[0];
  const tab = TAB_KEYS.includes(segment) ? segment : "accounts";

  const load = async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([listAccounts(), reconciliationSummary()]);
      setAccounts(Array.isArray(a.data?.accounts) ? a.data.accounts : []);
      setPresets(Array.isArray(a.data?.presets) ? a.data.presets : []);
      setSummary(s.data);
    } catch (err) {
      message.error(errorText(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div>
      <PageHeader
        title={t("Bank & payments")}
        subtitle={t("Bank accounts, statement import, payment matching and SEPA payment runs")}
      />
      <ReconciliationCard summary={summary} />
      <Tabs
        activeKey={tab}
        onChange={key => history.push(`/user/accounting/bank/${key}`)}
        items={[
          {
            key: "accounts",
            label: t("Accounts"),
            children: (
              <AccountsTab
                accounts={accounts}
                presets={presets}
                loading={loading}
                canWrite={canWrite}
                onChanged={load}
              />
            )
          },
          {
            key: "transactions",
            label: t("Transactions"),
            children: <TransactionsTab accounts={accounts} canWrite={canWrite} onChanged={load} />
          },
          {
            key: "payments",
            label: t("Payments"),
            children: <PaymentsTab accounts={accounts} canWrite={canWrite} onChanged={load} />
          }
        ]}
      />
    </div>
  );
};

export default Bank;
